import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Plus, FileText, Download, ArrowLeft, Edit2, Trash2, Calendar, DollarSign,
  Building2, Upload, Paperclip, Trash, FilePlus2, Loader2, Sparkles,
} from "lucide-react";
import api, { rowKey, fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ProjectTabs from "@/components/ProjectTabs";
import ContractChecklists from "@/components/contracts/ContractChecklists";
import ContractDossier from "@/components/contracts/ContractDossier";
import WorksNav from "@/components/WorksNav";
import ClientPicker from "@/components/ClientPicker";
import ContractEstimaciones from "@/components/ContractEstimaciones";
import ContractInvoices from "@/components/ContractInvoices";
import { toast } from "sonner";

// -- Catálogos --
const CONTRACT_TYPES = [
  { value: "original", label: "Original" },
  { value: "modificatorio", label: "Modificatorio" },
  { value: "convenio_adicional", label: "Convenio adicional" },
  { value: "diferimiento", label: "Diferimiento" },
];

const MODALITIES = [
  { value: "adjudicacion_directa", label: "Adjudicación directa" },
  { value: "invitacion_restringida", label: "Invitación restringida" },
  { value: "licitacion_publica", label: "Licitación pública" },
  { value: "contratacion_directa_privada", label: "Contratación directa (privada)" },
];

const FUNDING_SOURCES = [
  { value: "FAM", label: "FAM" },
  { value: "FAIS", label: "FAIS" },
  { value: "FORTAMUN", label: "FORTAMUN" },
  { value: "FORTASEG", label: "FORTASEG" },
  { value: "ISN", label: "ISN" },
  { value: "FONE", label: "FONE" },
  { value: "Recursos_Propios", label: "Recursos propios" },
  { value: "Privado", label: "Privado" },
  { value: "Otro", label: "Otro" },
];

const PAYMENT_FORMS = [
  { value: "estimaciones", label: "Estimaciones" },
  { value: "unico", label: "Pago único" },
  { value: "mixto", label: "Mixto" },
];

const STATUS = [
  { value: "borrador", label: "Borrador", color: "#94a3b8" },
  { value: "firmado", label: "Firmado", color: "#22d3ee" },
  { value: "ejecucion", label: "En ejecución", color: "#16a34a" },
  { value: "concluido", label: "Concluido", color: "#3F2A91" },
  { value: "rescindido", label: "Rescindido", color: "#dc2626" },
  { value: "cancelado", label: "Cancelado", color: "#64748b" },
];

function statusBadge(s) {
  const meta = STATUS.find((x) => x.value === s) || STATUS[0];
  return (
    <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest"
      style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}55` }}>
      {meta.label}
    </span>
  );
}

const EMPTY = {
  project_id: "",
  contracting_entity_id: "",
  type: "original",
  contract_number: "",
  modality: "adjudicacion_directa",
  funding_source: "Privado",
  funding_source_detail: "",
  pgo_number: "",
  admin_director_name: "",
  adjudication_reference: "",
  contractor: { name: "", legal_representative: "", legal_representative_title: "REPRESENTANTE LEGAL", address: "", rfc: "", registries: [] },
  beneficiary: { name: "", cct: "", locality: "", municipality: "", state: "", address: "" },
  description: "",
  subtotal: 0,
  iva_pct: 16,
  advances: [],
  payment_form: "estimaciones",
  payment_form_detail: "",
  signing_date: todayLocal(),
  start_date: todayLocal(),
  end_date: "",
  signer_contratante: { name: "", title: "" },
  signer_contratista: { name: "", title: "" },
  status: "borrador",
};

// ---------------------------------------------------------------------------
// Lista de contratos
// ---------------------------------------------------------------------------
export default function Contracts() {
  const { id: routeProjectId } = useParams();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (routeProjectId) params.project_id = routeProjectId;
      else if (filterProject) params.project_id = filterProject;
      if (filterStatus) params.status = filterStatus;
      const [c, p] = await Promise.all([
        api.get("/contracts", { params }),
        api.get("/projects"),
      ]);
      setItems(c.data || []);
      setProjects(p.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filterProject, filterStatus, routeProjectId]); // eslint-disable-line

  const projectName = (id) => projects.find((p) => p.id === id)?.code || "—";

  return (
    <div data-testid="contracts-page">
      <PageHeader
        subtitle="Contratos con dependencias gubernamentales y clientes privados"
        title="Contratos"
        actions={
          <Link
            to="/contracts/new"
            data-testid="new-contract-btn"
            className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
          >
            <Plus className="w-4 h-4" /> Nuevo contrato
          </Link>
        }
      />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        {routeProjectId ? <ProjectTabs projectId={routeProjectId} active="contracts" /> : <WorksNav active="contracts" />}
        <div className="panel p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {!routeProjectId && (
          <select data-testid="filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
            className="input-cyber px-3 py-2 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos los proyectos</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} · {p.name}</option>
            ))}
          </select>
          )}
          <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="input-cyber px-3 py-2 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos los estados</option>
            {STATUS.map((s) => (
              <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>
            ))}
          </select>
          <div className="text-xs text-slate-400 self-center text-right">
            {items.length} contrato{items.length !== 1 ? "s" : ""}
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-10">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="panel p-10 text-center text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            No hay contratos. Crea el primero para empezar.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((c) => (
              <Link
                key={c.id}
                to={`/contracts/${c.id}`}
                data-testid={`contract-card-${c.id}`}
                className="panel p-4 transition-all hover:border-cyan-500/60"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>
                      {projectName(c.project_id)} · {FUNDING_SOURCES.find((f) => f.value === c.funding_source)?.label}
                    </div>
                    <h3 className="font-display font-bold text-sm text-slate-100 truncate mt-1">{c.contract_number}</h3>
                  </div>
                  {statusBadge(c.status)}
                </div>
                <div className="text-xs text-slate-400 line-clamp-2 mb-2">
                  {c.description}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="text-slate-400">
                    {c.start_date && c.end_date && (
                      <span><Calendar className="w-3 h-3 inline mr-1" />{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
                    )}
                  </div>
                  <div className="font-mono font-bold tabular-nums text-cyan-300">{fmtMXN(c.total)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Crear / editar contrato (página completa)
// ---------------------------------------------------------------------------
export function ContractForm() {
  const { id } = useParams();
  const editing = id && id !== "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registryInput, setRegistryInput] = useState("");
  const [previewWords, setPreviewWords] = useState("");
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useState(null);

  const handleScan = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 15 MB)");
      return;
    }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contracts/scan-caratula", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Pre-llenar el formulario con TODO lo extraído, respetando el project_id ya elegido
      setForm((prev) => ({
        ...prev,
        contract_number: data.contract_number || prev.contract_number,
        modality: data.modality || prev.modality,
        funding_source: data.funding_source || prev.funding_source,
        funding_source_detail: data.funding_source_detail || prev.funding_source_detail,
        pgo_number: data.pgo_number || prev.pgo_number,
        adjudication_reference: data.adjudication_reference || prev.adjudication_reference,
        contracting_entity_id: data.matched_contracting_entity_id || prev.contracting_entity_id,
        contractor: { ...prev.contractor, ...(data.contractor || {}) },
        beneficiary: { ...prev.beneficiary, ...(data.beneficiary || {}) },
        description: data.description || prev.description,
        subtotal: data.subtotal ?? prev.subtotal,
        iva_pct: data.iva_pct ?? prev.iva_pct,
        advances: (data.advances && data.advances.length > 0) ? data.advances : prev.advances,
        payment_form: data.payment_form || prev.payment_form,
        payment_form_detail: data.payment_form_detail || prev.payment_form_detail,
        signing_date: data.signing_date || prev.signing_date,
        start_date: data.start_date || prev.start_date,
        end_date: data.end_date || prev.end_date,
        signer_contratante: data.signer_contratante?.name
          ? { ...prev.signer_contratante, ...data.signer_contratante }
          : prev.signer_contratante,
        signer_contratista: data.signer_contratista?.name
          ? { ...prev.signer_contratista, ...data.signer_contratista }
          : prev.signer_contratista,
      }));
      const msgs = [];
      msgs.push(`✅ Datos extraídos`);
      if (data.matched_contracting_entity_id) {
        msgs.push("Contratante encontrado en tu catálogo");
      } else if (data.contracting_entity?.name) {
        msgs.push(`Contratante NO encontrado: "${data.contracting_entity.name.slice(0, 40)}…" — créalo en el catálogo`);
      }
      toast.success(msgs.join(" · "), { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo escanear la carátula");
    } finally {
      setScanning(false);
      event.target.value = "";
    }
  };

  // Calcular IVA y total dinámicamente
  const totals = useMemo(() => {
    const sub = parseFloat(form.subtotal) || 0;
    const pct = parseFloat(form.iva_pct) || 0;
    const iva = Math.round(sub * pct) / 100;
    return { subtotal: sub, iva_pct: pct, iva, total: Math.round((sub + iva) * 100) / 100 };
  }, [form.subtotal, form.iva_pct]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || []));
  }, []);

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    api.get(`/contracts/${id}`).then((r) => {
      const c = r.data;
      setForm({
        ...EMPTY,
        ...c,
        contractor: { ...EMPTY.contractor, ...(c.contractor || {}) },
        beneficiary: { ...EMPTY.beneficiary, ...(c.beneficiary || {}) },
        signer_contratante: { ...EMPTY.signer_contratante, ...(c.signer_contratante || {}) },
        signer_contratista: { ...EMPTY.signer_contratista, ...(c.signer_contratista || {}) },
      });
    }).finally(() => setLoading(false));
  }, [id, editing]);

  // Preview de importe en letras (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (totals.total > 0) {
        try {
          const r = await api.get("/contracts/utils/amount-in-words", { params: { amount: totals.total } });
          setPreviewWords(r.data.words);
        } catch { /* ignore */ }
      } else {
        setPreviewWords("");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [totals.total]);

  const addAdvance = () => setForm({ ...form, advances: [...(form.advances || []), { _k: rowKey(), pct: 10, concept: "" }] });
  const removeAdvance = (i) => setForm({ ...form, advances: form.advances.filter((_, idx) => idx !== i) });
  const updateAdvance = (i, key, val) => {
    const next = [...form.advances];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, advances: next });
  };

  const addRegistry = () => {
    const v = registryInput.trim();
    if (!v) return;
    setForm({ ...form, contractor: { ...form.contractor, registries: [...(form.contractor.registries || []), v] } });
    setRegistryInput("");
  };
  const removeRegistry = (i) => {
    setForm({ ...form, contractor: { ...form.contractor, registries: form.contractor.registries.filter((_, idx) => idx !== i) } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Selecciona el proyecto"); return; }
    if (!form.contract_number?.trim()) { toast.error("Falta el número de contrato"); return; }
    if (!form.description?.trim()) { toast.error("Falta la descripción"); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      payload.advances = (payload.advances || []).map(({ _k, ...rest }) => rest);
      // Limpiar campos vacíos opcionales para que no rompan validación
      if (!payload.contracting_entity_id) delete payload.contracting_entity_id;
      if (editing) {
        await api.patch(`/contracts/${id}`, payload);
        toast.success("Contrato actualizado");
        navigate(`/contracts/${id}`);
      } else {
        const { data } = await api.post("/contracts", payload);
        toast.success("Contrato creado");
        navigate(`/contracts/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Cargando…</div>;

  return (
    <div data-testid="contract-form-page">
      <PageHeader
        subtitle={editing ? "Editar contrato" : "Nuevo contrato"}
        title={editing ? form.contract_number || "Editar contrato" : "Nuevo contrato"}
        actions={
          <Link to="/contracts" className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        }
      />
      <form onSubmit={handleSubmit} className="px-6 lg:px-8 py-6 space-y-5 max-w-5xl">

        {/* IA: Escanear carátula */}
        {!editing && (
          <div className="panel-strong p-4 flex items-center gap-3"
            style={{ borderColor: "rgba(34,211,238,0.4)", background: "linear-gradient(135deg, rgba(34,211,238,0.10), rgba(217,70,239,0.04))" }}>
            <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0"
              style={{ background: "rgba(34,211,238,0.18)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.4)" }}>
              {scanning ? <Loader2 className="w-6 h-6 animate-spin" /> : <Sparkles className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-mono mb-1" style={{ color: "var(--cyan)" }}>// CAPTURA RÁPIDA CON IA</div>
              <div className="text-sm text-slate-200 font-semibold">
                ¿Tienes la carátula del contrato en PDF o foto? Súbela y la IA llena el formulario por ti.
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                Lee número, modalidad, fuente, contratante, contratista, montos, anticipos, fechas y firmantes.
              </div>
            </div>
            <label className="btn-primary cursor-pointer px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2 shrink-0">
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {scanning ? "Procesando…" : "Escanear carátula"}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={handleScan}
                disabled={scanning}
                data-testid="scan-caratula-input"
              />
            </label>
          </div>
        )}

        {/* 1. Datos generales del contrato */}
        <Section title="DATOS GENERALES">
          <Grid cols={2}>
            <Field label="Proyecto *">
              <select required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                disabled={editing} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="" style={{ background: "#0a0f1d" }}>Selecciona…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} · {p.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Cliente">
              <ClientPicker
                value={form.contracting_entity_id || null}
                onChange={(id) => setForm({ ...form, contracting_entity_id: id || "" })}
                label=""
                placeholder="Buscar cliente…"
                allowEmpty
              />
            </Field>
            <Field label="Número de contrato *">
              <input required value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                placeholder="FAM-BÁSICO-2025-REM/012/2026/A.D."
                className="input-cyber px-3 py-2 text-sm w-full font-mono" />
            </Field>
            <Field label="Tipo">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full">
                {CONTRACT_TYPES.map((t) => <option key={t.value} value={t.value} style={{ background: "#0a0f1d" }}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Modalidad">
              <select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full">
                {MODALITIES.map((m) => <option key={m.value} value={m.value} style={{ background: "#0a0f1d" }}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Fuente de inversión">
              <select value={form.funding_source} onChange={(e) => setForm({ ...form, funding_source: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full">
                {FUNDING_SOURCES.map((f) => <option key={f.value} value={f.value} style={{ background: "#0a0f1d" }}>{f.label}</option>)}
              </select>
            </Field>
            <Field label="Detalle de la fuente">
              <input value={form.funding_source_detail || ""} onChange={(e) => setForm({ ...form, funding_source_detail: e.target.value })}
                placeholder="INFRAESTRUCTURA EN EDUCACIÓN BÁSICA 2025…"
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="No. P.G.O. de obra">
              <input value={form.pgo_number || ""} onChange={(e) => setForm({ ...form, pgo_number: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full font-mono" />
            </Field>
            <Field label="Director administrativo del cliente (firma la factura)">
              <input value={form.admin_director_name || ""} onChange={(e) => setForm({ ...form, admin_director_name: e.target.value.toUpperCase() })}
                placeholder="L.E. LORENZO ANTONIO REYES ANGULO"
                className="input-cyber px-3 py-2 text-sm w-full uppercase" data-testid="contract-admin-director" />
            </Field>
            <Field label="Referencia de adjudicación" wide>
              <input value={form.adjudication_reference || ""} onChange={(e) => setForm({ ...form, adjudication_reference: e.target.value })}
                placeholder="DG/ACUERDO/..."
                className="input-cyber px-3 py-2 text-sm w-full font-mono" />
            </Field>
            <Field label="Descripción de la obra *" wide>
              <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3} className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
          </Grid>
        </Section>

        {/* 2. Contratista (datos cada vez según preferencia) */}
        <Section title="CONTRATISTA (datos a llenar)">
          <Grid cols={2}>
            <Field label="Razón social" wide>
              <input value={form.contractor.name || ""}
                onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, name: e.target.value } })}
                placeholder="INCONTI, S.A. DE C.V."
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Representante legal">
              <input value={form.contractor.legal_representative || ""}
                onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, legal_representative: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Cargo">
              <input value={form.contractor.legal_representative_title || ""}
                onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, legal_representative_title: e.target.value } })}
                placeholder="REPRESENTANTE LEGAL"
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="RFC">
              <input value={form.contractor.rfc || ""}
                onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, rfc: e.target.value.toUpperCase() } })}
                className="input-cyber px-3 py-2 text-sm w-full font-mono" />
            </Field>
            <Field label="Domicilio">
              <input value={form.contractor.address || ""}
                onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, address: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Registros / Padrones" wide>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(form.contractor.registries || []).map((r, i) => (
                  <span key={`${r}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm font-mono"
                    style={{ background: "rgba(34,211,238,0.12)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.3)" }}>
                    {r}
                    <button type="button" onClick={() => removeRegistry(i)} className="ml-1 text-cyan-400 hover:text-red-400">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={registryInput} onChange={(e) => setRegistryInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRegistry(); } }}
                  placeholder="SDUOPOT-RUPC-1294-2024"
                  className="input-cyber px-3 py-2 text-sm flex-1 font-mono" />
                <button type="button" onClick={addRegistry} className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest">+ Agregar</button>
              </div>
            </Field>
          </Grid>
        </Section>

        {/* 3. Beneficiario / Obra */}
        <Section title="BENEFICIARIO / OBRA">
          <Grid cols={2}>
            <Field label="Nombre del beneficiario" wide>
              <input value={form.beneficiary.name || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, name: e.target.value } })}
                placeholder="C.E.P.I. JOSEFA ORTIZ DE DOMINGUEZ"
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="C.C.T. (clave centro escolar)">
              <input value={form.beneficiary.cct || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, cct: e.target.value.toUpperCase() } })}
                placeholder="12DCC1113F"
                className="input-cyber px-3 py-2 text-sm w-full font-mono" />
            </Field>
            <Field label="Localidad">
              <input value={form.beneficiary.locality || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, locality: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Municipio">
              <input value={form.beneficiary.municipality || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, municipality: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Estado">
              <input value={form.beneficiary.state || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, state: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Dirección adicional" wide>
              <input value={form.beneficiary.address || ""}
                onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, address: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
          </Grid>
        </Section>

        {/* 4. Montos */}
        <Section title="MONTOS (MXN)">
          <Grid cols={3}>
            <Field label="Subtotal *">
              <input type="number" step="0.01" min="0" required value={form.subtotal}
                onChange={(e) => setForm({ ...form, subtotal: parseFloat(e.target.value) || 0 })}
                className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" />
            </Field>
            <Field label="% IVA">
              <input type="number" step="0.01" min="0" max="100" value={form.iva_pct}
                onChange={(e) => setForm({ ...form, iva_pct: parseFloat(e.target.value) || 0 })}
                className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" />
            </Field>
            <Field label="Total (calculado)">
              <div className="input-cyber px-3 py-2 text-sm w-full text-right font-mono font-bold text-cyan-300">
                {fmtMXN(totals.total)}
              </div>
            </Field>
          </Grid>
          <div className="text-xs text-slate-400 mt-2">
            <b className="text-slate-300">IVA calculado:</b> {fmtMXN(totals.iva)}
            <span className="ml-3"><b className="text-slate-300">Importe en letras:</b> {previewWords || "—"}</span>
          </div>
        </Section>

        {/* 5. Anticipos */}
        <Section title="ANTICIPOS" actions={
          <button type="button" onClick={addAdvance} className="btn-ghost px-3 py-1 text-xs uppercase tracking-widest flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Agregar anticipo
          </button>
        }>
          {form.advances.length === 0 ? (
            <div className="text-xs text-slate-500 italic">Sin anticipos. Agrega uno si el contrato los contempla (ej. 10% inicio + 20% materiales).</div>
          ) : (
            <div className="space-y-2">
              {form.advances.map((a, i) => (
                <div key={a._k || i} className="grid grid-cols-12 gap-2 items-center">
                  <input type="number" step="0.01" min="0" max="100" value={a.pct}
                    onChange={(e) => updateAdvance(i, "pct", parseFloat(e.target.value) || 0)}
                    className="input-cyber px-3 py-2 text-sm col-span-2 text-right font-mono" placeholder="%" />
                  <input value={a.concept} onChange={(e) => updateAdvance(i, "concept", e.target.value)}
                    placeholder="Concepto (ej. PARA INICIO DE LOS TRABAJOS)"
                    className="input-cyber px-3 py-2 text-sm col-span-9" />
                  <button type="button" onClick={() => removeAdvance(i)}
                    className="btn-ghost p-2 rounded-sm col-span-1" style={{ color: "#fca5a5" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 6. Fechas + forma de pago */}
        <Section title="FECHAS Y FORMA DE PAGO">
          <Grid cols={3}>
            <Field label="Fecha de firma">
              <input type="date" value={form.signing_date || ""} onChange={(e) => setForm({ ...form, signing_date: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Fecha de inicio">
              <input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Fecha de término">
              <input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Forma de pago">
              <select value={form.payment_form} onChange={(e) => setForm({ ...form, payment_form: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full">
                {PAYMENT_FORMS.map((p) => <option key={p.value} value={p.value} style={{ background: "#0a0f1d" }}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Detalle de la forma de pago" wide>
              <input value={form.payment_form_detail || ""} onChange={(e) => setForm({ ...form, payment_form_detail: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
          </Grid>
        </Section>

        {/* 7. Firmantes */}
        <Section title="FIRMANTES">
          <Grid cols={2}>
            <Field label="Por LA CONTRATANTE — Nombre">
              <input value={form.signer_contratante?.name || ""}
                onChange={(e) => setForm({ ...form, signer_contratante: { ...form.signer_contratante, name: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Por LA CONTRATANTE — Cargo">
              <input value={form.signer_contratante?.title || ""}
                onChange={(e) => setForm({ ...form, signer_contratante: { ...form.signer_contratante, title: e.target.value } })}
                placeholder="DIRECTOR GENERAL"
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Por LA CONTRATISTA — Nombre">
              <input value={form.signer_contratista?.name || ""}
                onChange={(e) => setForm({ ...form, signer_contratista: { ...form.signer_contratista, name: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Por LA CONTRATISTA — Cargo">
              <input value={form.signer_contratista?.title || ""}
                onChange={(e) => setForm({ ...form, signer_contratista: { ...form.signer_contratista, title: e.target.value } })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Estatus del contrato">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full">
                {STATUS.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
              </select>
            </Field>
          </Grid>
        </Section>

        <div className="flex justify-end gap-2 sticky bottom-0 py-3" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}>
          <Link to="/contracts" className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</Link>
          <button type="submit" data-testid="contract-submit" disabled={saving}
            className="btn-primary px-6 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {editing ? "Guardar cambios" : "Crear contrato"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detalle del contrato (vista de solo lectura + carátula + documentos)
// ---------------------------------------------------------------------------
export function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [project, setProject] = useState(null);
  const [entity, setEntity] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("resumen");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setC(data);
      const proms = [
        api.get(`/projects/${data.project_id}`).then((r) => setProject(r.data)).catch(() => {}),
        api.get(`/budget/${data.project_id}/items`).then((r) => setBudgetItems(r.data?.items || r.data || [])).catch(() => {}),
      ];
      if (data.contracting_entity_id) {
        proms.push(api.get(`/contracting-entities/${data.contracting_entity_id}`).then((r) => setEntity(r.data)).catch(() => {}));
      }
      await Promise.all(proms);
    } catch {
      toast.error("Contrato no encontrado");
      navigate("/contracts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const downloadCaratula = async () => {
    try {
      const res = await api.get(`/contracts/${id}/caratula/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const safe = (c.contract_number || id).replace(/[/\s]/g, "_");
      link.download = `caratula_${safe}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Carátula descargada");
    } catch {
      toast.error("No se pudo generar la carátula");
    }
  };

  const handleUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      fd.append("name", file.name);
      await api.post(`/contracts/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento subido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!confirm(`¿Eliminar ${doc.name}?`)) return;
    try {
      await api.delete(`/contracts/${id}/documents/${doc.file_id}`);
      toast.success("Documento eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const handleDelete = async () => {
    if (!confirm("¿Eliminar este contrato? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contrato eliminado");
      navigate("/contracts");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (loading || !c) return <div className="p-10 text-center text-slate-400">Cargando…</div>;

  return (
    <div data-testid="contract-detail-page">
      <PageHeader
        subtitle={project ? `${project.code} · ${project.name}` : ""}
        title={c.contract_number}
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="download-caratula-btn" onClick={downloadCaratula}
              className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> Carátula PDF
            </button>
            <Link to={`/contracts/${id}/edit`} className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
              <Edit2 className="w-3.5 h-3.5" /> Editar
            </Link>
            <button onClick={handleDelete} className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
              style={{ borderColor: "#dc2626", color: "#fca5a5" }}>
              <Trash2 className="w-3.5 h-3.5" /> Eliminar
            </button>
          </div>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5 max-w-5xl">
        <div className="flex items-center gap-3 flex-wrap">
          {statusBadge(c.status)}
          <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
            {CONTRACT_TYPES.find((t) => t.value === c.type)?.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest font-mono"
            style={{ color: "var(--magenta)" }}>
            {FUNDING_SOURCES.find((f) => f.value === c.funding_source)?.label}
          </span>
          {entity?.auditable && (
            <span className="text-[10px] uppercase tracking-widest font-mono"
              style={{ color: "#fbbf24" }}>
              ★ AUDITABLE — {entity.audit_entity}
            </span>
          )}
        </div>

        <div className="flex gap-1 flex-wrap border-b pb-2" style={{ borderColor: "var(--border)" }}>
          {[["resumen", "Resumen"], ["expediente", "Expediente Digital"], ["estimaciones", "Estimaciones"]].map(([tid, tl]) => (
            <button key={tid} data-testid={`contract-tab-${tid}`} onClick={() => setTab(tid)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm ${tab === tid ? "btn-primary" : "btn-ghost"}`}>
              {tl}
            </button>
          ))}
        </div>

        {tab === "resumen" && (<>
        {/* Resumen de montos */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Kpi label="Subtotal" value={fmtMXN(c.subtotal)} />
          <Kpi label={`IVA (${c.iva_pct}%)`} value={fmtMXN(c.iva_amount)} />
          <Kpi label="Total" value={fmtMXN(c.total)} highlight />
          <Kpi label="Avance" value={`${project?.progress || 0}%`} />
        </div>
        <div className="panel p-3">
          <div className="label-mono mb-1">// IMPORTE EN LETRA</div>
          <div className="text-sm text-slate-200">{c.amount_in_words}</div>
        </div>

        {/* Datos del proyecto */}
        <Box title="Contratante">
          {entity ? (
            <div className="space-y-1 text-sm">
              <div className="font-bold text-slate-100">{entity.name}</div>
              {entity.legal_representative && (
                <div className="text-slate-400">{entity.legal_representative_title || "Rep. Legal"}: <span className="text-slate-200">{entity.legal_representative}</span></div>
              )}
              {entity.rfc && <div className="text-slate-400">RFC: <span className="font-mono text-slate-200">{entity.rfc}</span></div>}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">Sin contratante asignado.</div>
          )}
        </Box>

        <Box title="Contratista (en el contrato)">
          <div className="space-y-1 text-sm">
            <div className="font-bold text-slate-100">{c.contractor?.name || "—"}</div>
            {c.contractor?.legal_representative && (
              <div className="text-slate-400">
                {c.contractor.legal_representative_title}: <span className="text-slate-200">{c.contractor.legal_representative}</span>
              </div>
            )}
            {c.contractor?.address && <div className="text-slate-400">{c.contractor.address}</div>}
            {c.contractor?.rfc && <div className="text-slate-400">RFC: <span className="font-mono text-slate-200">{c.contractor.rfc}</span></div>}
            {(c.contractor?.registries || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {c.contractor.registries.map((r, i) => (
                  <span key={`${r}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono"
                    style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.25)" }}>{r}</span>
                ))}
              </div>
            )}
          </div>
        </Box>

        <Box title="Beneficiario / Obra">
          <div className="space-y-1 text-sm">
            {c.beneficiary?.name && <div className="font-bold text-slate-100">{c.beneficiary.name}</div>}
            {c.beneficiary?.cct && <div className="text-slate-400">C.C.T.: <span className="font-mono text-slate-200">{c.beneficiary.cct}</span></div>}
            <div className="text-slate-400">{[c.beneficiary?.locality, c.beneficiary?.municipality, c.beneficiary?.state].filter(Boolean).join(", ") || "—"}</div>
            <div className="text-slate-300 mt-2">{c.description}</div>
          </div>
        </Box>

        <Box title="Anticipos y forma de pago">
          {c.advances?.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {c.advances.map((a, i) => (
                <div key={`${a.pct}-${a.concept}-${i}`} className="text-sm">
                  <span className="font-mono font-bold text-cyan-300">{a.pct}% + I.V.A.</span>
                  <span className="text-slate-400 ml-2">{a.concept}</span>
                  {a.paid && <span className="ml-2 text-[10px] text-green-400">PAGADO</span>}
                </div>
              ))}
            </div>
          )}
          <div className="text-sm text-slate-300">
            {PAYMENT_FORMS.find((p) => p.value === c.payment_form)?.label}
            {c.payment_form_detail && <span className="text-slate-400"> · {c.payment_form_detail}</span>}
          </div>
        </Box>

        <Box title="Fechas">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Firma</div>
              <div className="text-slate-200 font-mono">{fmtDate(c.signing_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Inicio</div>
              <div className="text-slate-200 font-mono">{fmtDate(c.start_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Término</div>
              <div className="text-slate-200 font-mono">{fmtDate(c.end_date)}</div>
            </div>
          </div>
        </Box>
        </>)}

        {tab === "expediente" && (
          <ContractDossier contract={c} documents={c.documents || []} onUpload={handleUpload}
            onDeleteDoc={handleDeleteDoc} uploading={uploading} />
        )}

        {tab === "estimaciones" && (<>
        {/* Estimaciones */}
        <ContractEstimaciones contract={c} budgetItems={budgetItems} onRefresh={load} />
        <ContractInvoices contract={c} />
        </>)}
      </div>
    </div>
  );
}

// ===== UI primitives =====
function Section({ title, actions, children }) {
  return (
    <div className="panel">
      <div className="px-4 py-2.5 border-b flex items-center justify-between"
        style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.6)" }}>
        <div className="label-mono">// {title}</div>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>;
}
function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="label-mono block mb-1">{label}</label>
      {children}
    </div>
  );
}
function Kpi({ label, value, highlight }) {
  return (
    <div className="panel p-3">
      <div className="label-mono text-[10px]">{label}</div>
      <div className={`font-mono text-lg font-bold tabular-nums ${highlight ? "text-cyan-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}
function Box({ title, actions, children }) {
  return (
    <div className="panel">
      <div className="px-4 py-2.5 border-b flex items-center justify-between"
        style={{ borderColor: "var(--border)" }}>
        <div className="font-display font-bold text-sm text-slate-200">{title}</div>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
